import React, { useEffect, useState } from 'react';
import compose from 'helpers/compose';
import { observer, inject } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikHelpers } from 'formik'; //FormikProps
import merge from 'lodash.merge';
import { DateTime } from 'luxon';

import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import DatePicker from 'components/FormFields/FormDatePicker';
import { sortCountries } from 'helpers/helpers';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import ControlButtons from 'components/ControlButtons';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import FormTextField from 'components/FormFields/FormTextField';
import FormSelectField from 'components/FormFields/FormSelectField';
import Switch from 'components/FormFields/Switch';
//import RequiredDocUpload from 'components/RequiredDocUpload/RequiredDocUpload';
import FileUpload from 'components/FormFields/FileUpload';

import { CompanyOwnersStore } from 'stores/workflowStores/workflowAMLStore/companyOwnersStore';
import { initialValuesCompanyOwner, CompanyOwner } from 'stores/workflowStores/workflowAMLStore/model';
import Address from 'pages/Accounts/Edit/Contract/PersonalData/components/Address';
import { RootStore } from 'stores/rootStore';
import { Method } from 'stores/apiStore';
import { API_ACCOUNT_CONTRACT_AML_DOC_PEP_LEGAL } from 'api/endpoints';
import { EdwardColors } from 'enums';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface OwnProps {
  accountId: string;
  opened: boolean;
  handleClose: () => void;
  ownerId?: string;
  refresh: () => void;
  options: {
    countryCode: string[];
    ownerRelationship: string[];
    gender: string[];
  };
}

interface Stores {
  ownersStore: CompanyOwnersStore;
  rootStore?: RootStore;
}

type Props = OwnProps & Stores;

const CompanyOwnerModal: React.FC<Props> = ({
  accountId,
  ownerId,
  opened,
  handleClose,
  ownersStore,
  rootStore,
  refresh,
  options,
}) => {
  const { t } = useTranslation();
  let formSubmitting = false;
  const formKey = 'CompanyOwnerForm';
  const { fetchOwner, handleSubmit, data } = ownersStore;
  const [uploadFile, setUploadFile] = useState<null | File>(null);
  const [fileMissing, setFileMissing] = useState(false);

  useEffect(() => {
    if (opened && ownerId) {
      fetchOwner(ownerId);
    }
  }, [ownerId, fetchOwner, opened]);

  const onUploadChange = (e: any) => {
    setUploadFile(e.target.files[0]);
    setFileMissing(false);
  };

  const upload = async (ownerId: string) => {
    const formData = new FormData();
    formData.append('filename', uploadFile!);
    rootStore!.stores.uiStore.setLoading(true);
    const response = await rootStore!.stores.apiStore.handleRequest<File>(
      API_ACCOUNT_CONTRACT_AML_DOC_PEP_LEGAL(accountId, ownerId),
      Method.POST,
      formData,
      {
        method: Method.POST,
        body: formData,
        headers: {
          Authorization: `Bearer ${rootStore!.stores.apiStore.authToken}`,
          // No Content-Type: https://stackoverflow.com/a/49510941
        },
      },
    );
    rootStore!.stores.uiStore.setLoading(false);
    return !response?.errors;
  };

  const onSubmit = async (values: CompanyOwner, formikHelpers: FormikHelpers<CompanyOwner>) => {
    if (values.pepData.politicallyExposedPerson && !uploadFile && !values.document?.pep) {
      rootStore!.stores.errorStore.addGlobalError('globalErrors_validation');
      setFileMissing(true);
      return;
    }

    const data = await handleSubmit(values, formikHelpers);
    if (data?.id) {
      if (data.pepData.politicallyExposedPerson && uploadFile) {
        const ok = await upload(data.id);
        if (ok) {
          handleClose();
          refresh();
        }
      } else {
        handleClose();
        refresh();
      }
    }
  };

  return (
    <>
      <Dialog maxWidth="md" open={opened}>
        <DialogHeader onClose={handleClose}>{t(`contract_aml_legal_owner_${ownerId ? 'edit' : 'add'}`)}</DialogHeader>
        <DialogContent>
          <Formik
            initialValues={data && ownerId ? merge({}, initialValuesCompanyOwner, data) : initialValuesCompanyOwner}
            onSubmit={onSubmit}
            enableReinitialize
          >
            {(formikProps) => {
              formSubmitting = formikProps.isSubmitting;
              return (
                <FormWithoutEnterSubmit id={t(formKey)}>
                  <Grid container>
                    <Grid item xs={4}>
                      <FormSelectField
                        name={`relationship`}
                        label={t('general_relationship')}
                        options={options.ownerRelationship.map((r) => ({
                          value: r,
                          label: t(`options_relationship_${r}`),
                        }))}
                      />
                    </Grid>
                    <Grid item xs={8} />
                    <Grid item xs={4}>
                      <FormTextField name={`firstname`} label={t('general_name')} />
                    </Grid>
                    <Grid item xs={4}>
                      <FormTextField name={`lastname`} label={t('general_surname')} />
                    </Grid>
                    <Grid item xs={4}>
                      <FormSelectField
                        name={`gender`}
                        label={t('general_gender')}
                        options={options.gender.map((gender) => ({
                          value: gender,
                          label: t(`gender_${gender}`),
                        }))}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <DatePicker
                        maxDate={DateTime.local().minus({ years: 18 })} // * it still shows now-17 year, but it can't be used
                        minDate={DateTime.local(1900, 1, 1)}
                        name={`birthDate`}
                        label={t('general_birthDate')}
                      />
                    </Grid>{' '}
                    <Grid item xs={4}>
                      <FormTextField name={`placeOfBirth`} label={t('general_placeOfBirth')} />
                    </Grid>
                    <Grid item xs={4}>
                      <FormSelectField
                        name={`citizenship`}
                        label={t('general_citizenship')}
                        options={sortCountries(
                          options.countryCode.map((country) => ({
                            value: country,
                            label: t(`country_${country}`),
                          })),
                        )}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormSelectField
                        name="taxResidency"
                        label={t('contract_taxInfo_taxResidency')}
                        options={sortCountries(
                          options.countryCode.map((code) => ({
                            value: code,
                            label: t(`country_${code}`),
                          })),
                        )}
                      />
                    </Grid>
                    {(formikProps.values.relationship === 'PARTNER' ||
                      (formikProps.values.taxResidency !== '' && formikProps.values.taxResidency !== 'CZE')) && (
                      <Grid item xs={4}>
                        <FormTextField
                          name="taxNumber"
                          label={t('contract_taxInfo_number')}
                          tooltipContent={
                            <span
                              dangerouslySetInnerHTML={{
                                __html: t('contract_taxInfo_number_tooltip', {
                                  interpolation: { escapeValue: false },
                                }),
                              }}
                            />
                          }
                          maxLength={20}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} />
                    <Grid item xs={8}>
                      <Switch
                        name="pepData.politicallyExposedPerson"
                        label={t(`contract_aml_pep_legal`)}
                        afterChange={() => setFileMissing(false)}
                      />
                    </Grid>
                    {formikProps.values.pepData.politicallyExposedPerson && (
                      <>
                        <Grid item xs={6}>
                          <FormTextField
                            name="pepData.politicallyExposedDetail"
                            label={t(`contract_aml_politicallyExposedDetail_legal`)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          {t('contract_aml_pep_info1')}
                          <ul style={{ width: '60%' }}>
                            <li>{t('contract_aml_pep_opt1')}</li>
                          </ul>
                          {t('contract_aml_pep_info2')}
                        </Grid>

                        <Grid item xs={12}>
                          <Divider />
                        </Grid>

                        <Grid item xs={3} style={{ overflow: 'hidden ' }}>
                          <FileUpload
                            name={'document'}
                            accept=".jpg,.png,.pdf"
                            onChange={(e: any) => onUploadChange(e)}
                            //upload={upload}
                            specificError
                            errors={fileMissing ? { 'document': 'account.aml.document.pep.missing' } : undefined}
                            vertical
                            accepted={t('contract_aml_file_info')}
                          />
                        </Grid>
                        <Grid item xs={9} style={{ lineHeight: '36px' }}>
                          {uploadFile === null && !formikProps.values.document?.pep && t('contract_aml_file_info')}
                          <span style={{ color: EdwardColors.GREEN, fontSize: '19px' }}>
                            {formikProps.values.document?.pep && (
                              <>
                                <CheckCircleIcon style={{ verticalAlign: 'text-bottom', marginRight: '8px' }} />
                                {t('general_uploaded')}
                              </>
                            )}
                            {uploadFile?.name && <>{t('form_fileUpload_selected')}:</>}
                            {(uploadFile?.name || formikProps.values.document?.pep) && (
                              <span style={{ color: '#000' }}>
                                &nbsp;{uploadFile?.name || formikProps.values.document?.pep}
                              </span>
                            )}
                          </span>
                        </Grid>

                        <Grid item xs={12}>
                          <Divider />
                        </Grid>
                      </>
                    )}
                  </Grid>

                  <Grid container>
                    <Grid item xs={12}>
                      <Typography variant="h3">{t('contract_permanentAddress')}</Typography>
                    </Grid>
                  </Grid>
                  <Address prefix="address" addressType="" countries={options.countryCode} />
                </FormWithoutEnterSubmit>
              );
            }}
          </Formik>
        </DialogContent>
        <DialogActions>
          <ControlButtons
            isSubmitting={formSubmitting}
            nextText={t('general_save')}
            form={formKey}
            nextButtonId="companyOwner-modal-submit"
            backText={t('general_cancel')}
            onBack={handleClose}
            onClick={() => {}}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default compose(inject('rootStore'), observer)(CompanyOwnerModal);
